import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { X } from 'react-feather';
import NotificationUnReadIcon from 'assets/icons/NotificationUnReadIcon';
import NotificationReadIcon from 'assets/icons/NotificationReadIcon';
import NotiImgDefault from 'assets/images/noti_img_default.png';
import moment from 'moment';
import toast, { Toaster } from 'react-hot-toast';
import { NotificationService } from 'core/services/notification.service';
import { GenealogyService } from 'core/services/genealogy.service';
import { ModalEventDetail } from 'pages/event/component/ModalEventDetailComponent';
import palette from 'core/constant/palette';

/**
 * The header's notification bell: the icon, its dropdown of the latest
 * notifications, the toast cards it raises for events coming due, and the event
 * modal a notification opens. All of the fetching and the notification state
 * lives here rather than in NavBar, which now only decides whether to show it
 * ({user && <NotificationBell />}). Lifted out of NavBar unchanged (BLD-11).
 */

// The list is fetched once, 100 at a time; there is no paging control, so the
// page is a constant rather than state that nothing ever set.
const NOTIFICATION_PAGE = 1;

const readNotificationById = async (id: string) => {
    await NotificationService.readById(id);
};

const showAlertNotification = async (id: string, params: any) => {
    await NotificationService.showAlert(id, params);
};

const daysDifference = (createdDate: any) => {
    const today = new Date();
    const createdDateMoment = new Date(createdDate);
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const comparisonDateWithoutTime = new Date(createdDateMoment.getFullYear(), createdDateMoment.getMonth(), createdDateMoment.getDate());
    const todayWithoutTime = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const createdDateMilliseconds = comparisonDateWithoutTime.getTime();
    const todayMilliseconds = todayWithoutTime.getTime();

    const diffDate = Math.floor((todayMilliseconds - createdDateMilliseconds) / millisecondsPerDay);
    return diffDate
};

const NotificationBell = () => {
    const [notifications, setNotifications] = useState<any>([]);
    const [isHaveUnreadNotifications, setIsHaveUnreadNotifications] = useState<boolean>(false);
    const [isOpenEventModal, setIsOpenEventModal] = useState<boolean>(false);
    const [eventData, setEventData] = useState<any>();

    // Both handlers are memoised so the effects below can list them as
    // dependencies without re-running on every render: getNotifications only
    // touches state setters, and getEventDetail only that plus getNotifications.
    const getNotifications = useCallback(async (pageNumber: number, isLoadMore: boolean) => {
        const params: any = {
            PageNumber: pageNumber,
            PageSize: 100
        }
        const res = await NotificationService.getList(params);

        if (res?.data?.isSuccess === true) {
            let isUnread = false;
            // let isUnreadNoti: any = [];
            const notificationMap = res?.data?.data?.records?.map((notification: any) => {
                const extraData = notification?.extraData ? JSON.parse(notification?.extraData) : undefined;
                const notificationData = {
                    id: notification?.id,
                    eventId: notification?.referenceDocumentId || '0cc8f9fa-6dd0-4c56-a7e4-9946d1e68514',
                    image: notification?.imageUrl || NotiImgDefault,
                    clan: extraData?.ClanName,
                    lineage: extraData?.LineageName,
                    eventname: extraData?.EventName,
                    eventSolarDate: extraData?.EventDate,
                    eventLunarDate: extraData?.EventLunarDate,
                    createdDate: notification?.createdDate,
                    isRead: notification?.isRead,
                    isAlertCreated: notification?.isAlertCreated,
                    isAlertDaysLeft: notification?.isAlertDaysLeft
                };
                if (notification?.isRead === false) {
                    isUnread = true;
                    // isUnreadNoti.push(notificationData);
                }
                return notificationData
            });

            setNotifications([
                ...notificationMap,
            ]);
            setIsHaveUnreadNotifications(isUnread);
            // if (isUnreadNoti?.length > 0) {
            //     setNotificationIsReadFalse(isUnreadNoti);
            // }
        }
    }, []);

    const getEventDetail = useCallback(async (notification: any) => {
        if (!notification?.isRead) {
            readNotificationById(notification?.id);
            setTimeout(() => {
                getNotifications(NOTIFICATION_PAGE, false);
            }, 500)
        }
        const res = await GenealogyService.getEventById(notification?.eventId);
        if (res?.data?.isSuccess === true) {
            setEventData(res?.data?.data);
            setIsOpenEventModal(true);
        }
    }, [getNotifications]);

    useEffect(() => {
        getNotifications(NOTIFICATION_PAGE, false);
    }, [getNotifications]);

    useEffect(() => {
        if (notifications?.length > 0) {
            const maxNoti = notifications?.length > 3 ? 3 : notifications?.length;
            for (let index = 0; index < maxNoti; index++) {
                const diffDate = notifications[index]?.eventSolarDate ?  daysDifference(notifications[index]?.eventSolarDate) : undefined;
                let isShow = false;
                if ((diffDate !== undefined) && diffDate <= 0) {
                    const day = Math.abs(diffDate);

                    if (day <= 3 && !notifications[index].isAlertDaysLeft) {
                        const params = {
                            isAlertDaysLeft: true,
                            isAlertCreated: notifications[index].isAlertCreated
                        };
                        showAlertNotification(notifications[index].id, params);
                        isShow = true;
                    }else if (day > 3 && !notifications[index].isAlertCreated) {
                        const params = {
                            isAlertDaysLeft: notifications[index].isAlertDaysLeft,
                            isAlertCreated: true
                        };
                        showAlertNotification(notifications[index].id, params);
                        isShow = true;
                    }
                }
                if (isShow) {
                    toast.custom((t: any) => (
                        <div className='relative flex bg-gold-wash rounded shadow-lg w-[437px] px-5 pt-7 pb-4 text-brand'>
                            {/* The card itself carried the onClick, which put the
                                dismiss button inside a clickable region that was not
                                itself focusable. The action moves onto a real button
                                wrapping everything except the dismiss control -- one
                                cannot nest inside the other. */}
                            <button
                            type='button'
                            className='flex flex-1 gap-[22px] text-left cursor-pointer rounded
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-brand'
                            onClick={() => getEventDetail(notifications[index])}
                            >
                            <img src={notifications[index]?.image} alt="notiImg" className='w-[117px] h-[78px]'/>
                            <div className='flex-1'>
                                <div className='line-clamp-4'>
                                    {`Phả tộc Nguyễn Đức - ${notifications[index]?.clan} - ${notifications[index]?.lineage} sẽ có sự kiện mới `}
                                    <span className='font-bold'>{notifications[index]?.eventname}</span>
                                    {` được diễn ra vào ngày `}
                                    <span className='font-bold'>{`${moment(notifications[index].eventSolarDate).format('DD/MM/YYYY')} (tức ngày ${moment(notifications[index].eventLunarDate).format('DD/MM/YYYY')} Âm lịch) `}</span>
                                </div>
                                <div className=' text-xs text-link mt-1'>
                                    {`${(daysDifference(notifications[index].createdDate) <= 0) ? 'Hôm nay' : `${daysDifference(notifications[index].createdDate)} ngày`}`}
                                </div>
                            </div>
                            </button>
                            <button
                            type='button'
                            aria-label='Đóng thông báo'
                            className=' absolute top-1 right-1 cursor-pointer p-1 z-10 rounded
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-brand'
                            onClick={(event) => {event.stopPropagation(); toast.dismiss(t.id);}}
                            >
                                <X color={palette.gold} size={24}/>
                            </button>
                        </div>
                    ))
                }
            }
        }
    }, [notifications, getEventDetail])

    return (
        <>
            <div>
                <Menu as="div" className="relative inline-block">
                    {/* The button's only content is an icon, so until now it
                        announced itself as an unnamed button. */}
                    <Menu.Button
                    aria-label={isHaveUnreadNotifications ? 'Thông báo (có thông báo chưa đọc)' : 'Thông báo'}
                    className='rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-gold'
                    >
                        <div className='flex items-center gap-3 mt-1'>
                            {isHaveUnreadNotifications ?
                                <NotificationUnReadIcon/> : <NotificationReadIcon/>
                            }
                        </div>
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        {/* This was a plain `div`. `Menu.Button` opened it, but
                            without `Menu.Items` around the contents there was no
                            arrow-key movement, no Escape, no focus returning to
                            the button, and nothing telling a screen reader this
                            was a menu. */}
                        <Menu.Items className='absolute right-0 !z-50 overflow-auto hidden-scrollbar shadow top-[35px]
                        w-[427px] h-auto min-h-[220px] max-h-[538px] px-[25px] py-[23px] bg-gold-wash border border-secondary rounded-md
                        text-brand focus:outline-none'
                        >
                            <div className='text-brand font-semibold text-lg mb-[22px]'>Thông báo</div>
                            {notifications?.length === 0 &&
                                <div>Không có thông báo</div>
                            }
                            <div className=' space-y-[26px]'>
                                {notifications?.length > 0 &&
                                    notifications?.map((notification: any) => (
                                        <Menu.Item key={notification.id}>
                                        {({ active }: { active: boolean }) => (
                                        <button
                                        type='button'
                                        className={`flex w-full gap-[22px] text-left cursor-pointer rounded
                                          ${active ? 'ring-2 ring-brand' : ''}`}
                                        onClick={() => getEventDetail(notification)}
                                        >
                                            <img src={notification?.image} alt="notiImg" className='w-[117px] h-[78px]'/>
                                            <div className={`flex-1 relative ${notification?.isRead ? 'opacity-50' : ''}`}>
                                                <div className='line-clamp-4'>
                                                    {`Phả tộc Nguyễn Đức - ${notification?.clan} - ${notification?.lineage} sẽ có sự kiện mới `}
                                                    <span className='font-bold'>{notification?.eventname}</span>
                                                    {` được diễn ra vào ngày `}
                                                    <span className='font-bold'>{`${moment(notification.eventSolarDate).format('DD/MM/YYYY')} (tức ngày ${moment(notification.eventLunarDate).format('DD/MM/YYYY')} Âm lịch) `}</span>
                                                </div>
                                                <div className=' text-xs text-link mt-1'>
                                                    {`${(daysDifference(notification.createdDate) <= 0) ? 'Hôm nay' : `${daysDifference(notification.createdDate)} ngày`}`}
                                                </div>
                                            </div>
                                        </button>
                                        )}
                                        </Menu.Item>
                                    ))
                                }
                            </div>
                        </Menu.Items>
                    </Transition>
                </Menu>
            </div>
            {isOpenEventModal && (
                <ModalEventDetail
                open={isOpenEventModal}
                onClose={() => setIsOpenEventModal(false)}
                data={eventData}
                />
            )}
            <Toaster
            position="bottom-left"
            // On a phone the tree page fixes its view bar to the bottom of the
            // window and says how tall it is; everywhere else this is 16px.
            containerStyle={{ bottom: 'calc(16px + var(--tree-bar-height, 0px))' }}
            reverseOrder={false}
            toastOptions={
                {
                    className: 'bg-gold-wash rounded shadow-lg',
                    duration: 5000,
                }
            }
            />
        </>
    )
}

export default NotificationBell
